/*
 * ॐ नमः शिवाय
 *
 * Copyright (c) 2025 Rudra Coresoft Technologies Private Limited
 *
 * This software is licensed under the VIJAYA 1.0 License.
 * See the LICENSE file for the full text.
 */

/**
 * boardIndexModel — turning events and their races into the /results index.
 *
 * No network and no React, so the grouping rules can be read and tested on
 * their own.
 *
 * EVERY EVENT HAS A BOARD. The index lists them all rather than only the ones
 * that already have results: a month-long challenge has an empty board on day
 * one and a busy one on day thirty, and hiding it until somebody finishes would
 * hide it exactly when people are looking for it. The board itself says "no
 * results yet" far better than an absence from a list does.
 */

/** A value the fetch API may hand back expanded, as a bare id, or not at all. */
export function idOf(value) {
  if (value === null || value === undefined) return null;
  if (typeof value === 'object') {
    if (value.id !== undefined && value.id !== null) return value.id;
    if (value.data && value.data.id !== undefined) return value.data.id;
    return null;
  }
  return value;
}

/** The title on an event row, however the API shaped it. */
export function titleOf(event) {
  const row = event || {};
  return String(row.title || row.name || 'Event').trim();
}

/**
 * Where an event sits relative to now: 'live', 'upcoming' or 'past'.
 *
 * Live wins over everything — a board people can still affect is the one worth
 * putting at the top. An event with no dates at all reads as past rather than
 * live: a board nobody can place is better found by searching than by sitting
 * above events that are genuinely running.
 */
export function phaseOf(event, now = new Date()) {
  const start = event && event.start_datetime ? new Date(event.start_datetime) : null;
  const end = event && event.end_datetime ? new Date(event.end_datetime) : null;
  const startOk = start && !Number.isNaN(start.getTime());
  const endOk = end && !Number.isNaN(end.getTime());

  if (startOk && endOk) {
    if (now >= start && now <= end) return 'live';
    return now < start ? 'upcoming' : 'past';
  }
  if (startOk) return now < start ? 'upcoming' : 'live';
  if (endOk) return now <= end ? 'live' : 'past';
  return 'past';
}

/**
 * An event description as plain words.
 *
 * `event.description` is RICH TEXT — the organizer writes it in an editor, so
 * it arrives as HTML. Printed straight onto a card it reads
 * `<p>A flat <strong>10K</strong> through the old city.</p>`, tags and all,
 * which is what the index did before this. Rendering the HTML instead is not
 * the answer either: a card shows three lines, and somebody's h3 and bullet
 * list would drag the whole grid out of line.
 *
 * So: tags out, entities back to characters, whitespace collapsed. Deliberately
 * NOT dangerouslySetInnerHTML — this text is written by organizers and shown to
 * the public, and a card is not worth an injection surface.
 *
 * The saved query has already cut it to 400 characters, so this may be handed
 * a tag that was sliced in half; the trailing `<…` with no `>` is dropped too.
 */
export function plainTextOf(html) {
  if (!html) return null;
  const text = String(html)
    .replace(/<br\s*\/?>/gi, ' ')
    .replace(/<\/(p|div|li|h[1-6])>/gi, ' ')
    .replace(/<[^>]*>/g, '')
    // A tag the 400-character cut left unclosed.
    .replace(/<[^>]*$/, '')
    .replace(/&nbsp;/gi, ' ')
    .replace(/&amp;/gi, '&')
    .replace(/&lt;/gi, '<')
    .replace(/&gt;/gi, '>')
    .replace(/&quot;/gi, '"')
    .replace(/&#39;|&apos;/gi, "'")
    .replace(/\s+/g, ' ')
    .trim();
  return text || null;
}

/**
 * Events with their races attached, grouped and ordered for the index.
 *
 * Live first, then what is coming, then what has been — and inside each group,
 * nearest in time first, so the thing a visitor is most likely looking for is
 * nearest the top of its band.
 *
 * An event with no races is dropped: there is no board to open, so a row for it
 * would be a link to nothing.
 */
export function groupForIndex(events, races, now = new Date()) {
  // `races` is optional: the public API hands an event its own races, so the
  // page usually reads them per event and passes none here. When it does pass
  // a flat list — every race carrying its event_id — they are grouped onto
  // their events instead. Both shapes produce the same answer.
  const byEvent = new Map();
  (races || []).forEach((race) => {
    const eventId = idOf(race.event_id);
    if (eventId === null) return;
    const key = String(eventId);
    if (!byEvent.has(key)) byEvent.set(key, []);
    byEvent.get(key).push({
      id: race.id,
      name: String(race.name || 'Race'),
      resultsPublished: Boolean(race.results_published),
    });
  });

  const rows = (events || [])
    .map((event) => {
      const races = byEvent.get(String(event.id)) || event.races || [];
      return {
        id: event.id,
        title: titleOf(event),
        coverImageUrl: event.cover_image_url || null,
        description: plainTextOf(event.description),
        location: event.location || null,
        startDatetime: event.start_datetime || null,
        endDatetime: event.end_datetime || null,
        phase: phaseOf(event, now),
        races,
        // How many boards this event has, known from the event itself even when
        // the races have not been read. The index draws its cards from this and
        // fetches the race names only when a card is opened.
        raceCount: Number(event.raceCount ?? event.number_of_activities) || races.length,
      };
    })
    // An event with no board at all would be a link to nothing.
    .filter((event) => event.races.length > 0 || event.raceCount > 0);

  rows.forEach((event) => event.races.sort((a, b) => Number(a.id) - Number(b.id)));

  const timeOf = (event) => {
    const value = event.startDatetime || event.endDatetime;
    const at = value ? new Date(value).getTime() : NaN;
    return Number.isNaN(at) ? 0 : at;
  };

  return {
    live: rows.filter((e) => e.phase === 'live').sort((a, b) => timeOf(a) - timeOf(b)),
    upcoming: rows.filter((e) => e.phase === 'upcoming').sort((a, b) => timeOf(a) - timeOf(b)),
    past: rows.filter((e) => e.phase === 'past').sort((a, b) => timeOf(b) - timeOf(a)),
  };
}

/** Events whose title, or any of whose race names, contains `term`. */
export function searchEvents(events, term) {
  const needle = String(term || '').trim().toLowerCase();
  if (!needle) return events || [];
  return (events || []).filter((event) => (
    event.title.toLowerCase().includes(needle)
    || event.races.some((race) => race.name.toLowerCase().includes(needle))
  ));
}

/** "1–30 Nov 2026", "2 Nov 2026", or '' when there are no dates. */
export function datesOf(event) {
  const start = event.startDatetime ? new Date(event.startDatetime) : null;
  const end = event.endDatetime ? new Date(event.endDatetime) : null;
  const ok = (value) => value && !Number.isNaN(value.getTime());
  const day = { day: 'numeric', month: 'short', year: 'numeric' };

  if (ok(start) && ok(end)) {
    if (start.toDateString() === end.toDateString()) return start.toLocaleDateString(undefined, day);
    return `${start.toLocaleDateString(undefined, { day: 'numeric', month: 'short' })} – ${end.toLocaleDateString(undefined, day)}`;
  }
  if (ok(start)) return start.toLocaleDateString(undefined, day);
  if (ok(end)) return end.toLocaleDateString(undefined, day);
  return '';
}
